import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = (typeof d === 'string' ? d : '').slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};

const LABELS_MAP = {
  prima_dei_lavori: 'Prima dei lavori',
  in_corso: 'In corso',
  fine_lavori: 'Fine lavori',
  problema: 'Problema / Difetto',
  materiale: 'Materiale',
  altro: 'Altro',
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sectionHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiLabel: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  groupHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#0A1628', marginTop: 10, marginBottom: 4 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  imgCell: { width: 80, height: 80, borderRadius: 4, overflow: 'hidden', border: '0.5pt solid #e9ecef' },
  imgLabel: { fontSize: 6, color: '#868e96', marginTop: 2, width: 80 },
  placeholder: { width: 80, height: 80, borderRadius: 4, backgroundColor: '#f8f9fa', border: '0.5pt solid #e9ecef', justifyContent: 'center', alignItems: 'center' },
});

const MAX_FOTO = 30;

export default function FotoTabPDF({ foto, cantiere, azienda }) {
  const oggi = new Date().toLocaleDateString('it-IT');
  const allFoto = (foto || []).filter(f => {
    const mime = f.metadata?.mime_type || '';
    return !mime.includes('video');
  });
  const limited = allFoto.slice(0, MAX_FOTO);
  const truncated = allFoto.length > MAX_FOTO;

  // Breakdown per label
  const perLabel = {};
  allFoto.forEach(f => {
    const l = f.metadata?.label || 'altro';
    perLabel[l] = (perLabel[l] || 0) + 1;
  });

  const dates = allFoto.map(f => f.data_riferimento || f.created_at?.slice(0, 10) || '').filter(Boolean).sort();
  const prima = dates[0];
  const ultima = dates[dates.length - 1];

  // Raggruppa per label
  const grouped = {};
  limited.forEach(f => {
    const l = f.metadata?.label || 'altro';
    if (!grouped[l]) grouped[l] = [];
    grouped[l].push(f);
  });

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Documentazione Fotografica — {cantiere?.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{azienda?.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        <Text style={s.sectionHead}>Riepilogo</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Totale foto</Text>
            <Text style={s.kpiValue}>{allFoto.length}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Per stato</Text>
            <Text style={{ fontSize: 7 }}>{Object.entries(perLabel).map(([k, v]) => (LABELS_MAP[k] || k) + ': ' + v).join(', ')}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Prima / Ultima</Text>
            <Text style={{ fontSize: 7 }}>{fmtDate(prima)} — {fmtDate(ultima)}</Text>
          </View>
        </View>

        {Object.entries(grouped).map(([label, lista]) => (
          <View key={label}>
            <Text style={s.groupHead}>{LABELS_MAP[label] || label} ({lista.length})</Text>
            <View style={s.grid}>
              {lista.map(f => {
                const src = f.allegato_url || f.allegato;
                const dateLabel = fmtDate(f.data_riferimento || f.created_at?.slice(0, 10));
                return (
                  <View key={f.id} style={{ marginBottom: 6 }}>
                    {src ? (
                      <Image src={src} style={s.imgCell} />
                    ) : (
                      <View style={s.placeholder}>
                        <Text style={{ fontSize: 6, color: '#868e96' }}>Non disponibile</Text>
                      </View>
                    )}
                    <Text style={s.imgLabel}>{dateLabel}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        ))}

        {truncated && (
          <Text style={{ fontSize: 7, color: '#868e96', marginTop: 8, fontStyle: 'italic' }}>
            Mostrate {MAX_FOTO} foto su {allFoto.length} totali. Le restanti sono disponibili nella piattaforma.
          </Text>
        )}

        <View style={s.footer} fixed>
          <Text>mAIstro — Documentazione Fotografica</Text>
          <Text>{cantiere?.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}
